import { Box, Tooltip, Typography, styled } from '@mui/material';
import SvgIcon from '../../components/SvgIcon';
import { ReactComponent as TrashIcon } from '../../assets/icons/24/remove.svg';
import { ReactComponent as PlaceholderIcon } from '../../assets/icons/64/photo.svg';
import { CAR_FILE_TYPES } from '../../constants/file';
import { TOOLTIP_SLOT_PROPS } from '../../constants/tooltip';
import StatusBadge from '../../components/StatusBadge';
import { getCarStatusLabel, normalizeCarStatus } from '../../helpers/status';
import { getThemeColors } from '../../helpers/themeStyles';
import { THEME_MODES } from '../../theme';

const CarListContainer = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  gap: 16,
  alignContent: 'start',
  padding: '24px 24px 0',
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
});

const CarCard = styled(Box)(({ theme }) => {
  const colors = getThemeColors(theme);
  const isDarkMode = theme.palette.mode === THEME_MODES.DARK;

  return {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: 16,
    borderRadius: 20,
    backgroundColor: isDarkMode ? colors.surfaceElevated : colors.surfaceSecondary,
    border: `1px solid ${colors.borderSubtle}`,
    cursor: 'pointer',
    width: '100%',
    height: 152,
    boxSizing: 'border-box',
    transition: 'border-color 0.2s ease, background-color 0.2s ease',
    '&:hover': {
      borderColor: isDarkMode ? '#5a5a5a' : colors.borderSubtle,
      backgroundColor: isDarkMode ? '#363636' : theme.palette.background.paper,
    },
  };
});

const CarImage = styled(Box)(({ theme }) => ({
  width: 120,
  height: 120,
  minWidth: 120,
  flexShrink: 0,
  borderRadius: 16,
  backgroundColor:
    theme.palette.mode === THEME_MODES.DARK ? '#232323' : theme.palette.background.default,
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
}));

const CarInfo = styled(Box)({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
});

const CarName = styled(Typography)(({ theme }) => ({
  fontSize: 18,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.06,
  color: theme.palette.text.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const CarPlate = styled(Typography)(({ theme }) => ({
  fontSize: 13,
  fontWeight: 500,
  color: theme.palette.text.secondary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const getCarPhotoUrl = (car, carFiles = []) => {
  const fromCar = car.files?.find((f) => f.type === CAR_FILE_TYPES.FRONT_PHOTO);
  if (fromCar?.file?.url) return fromCar.file.url;
  const fromFiles = carFiles.find(
    (f) => String(f.carId) === String(car.id) && f.type === CAR_FILE_TYPES.FRONT_PHOTO
  );
  return fromFiles?.file?.url ?? null;
};

const RemoveButton = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 8,
  borderRadius: 8,
  cursor: 'pointer',
  color: theme.palette.text.secondary,
  flexShrink: 0,
  '&:hover': {
    color: '#FF6B6B',
  },
}));

const CarList = ({ cars, carFiles = [], onSelectCar, onRemoveCar }) => {
  if (!cars?.length) {
    return (
      <CarListContainer>
        <Typography sx={{ color: 'text.secondary', fontSize: 14, fontWeight: 500 }}>
          Aucun véhicule trouvé
        </Typography>
      </CarListContainer>
    );
  }

  return (
    <CarListContainer>
      {cars.map((car) => {
        const photoUrl = getCarPhotoUrl(car, carFiles);
        const displayName =
          [car.brand ?? car.marque, car.model ?? car.modele]
            .filter(Boolean)
            .join(' ')
            .toUpperCase() || 'Véhicule';
        const plate = car.plateNumber || '';
        const status = normalizeCarStatus(car);

        return (
          <CarCard key={car.id} onClick={() => onSelectCar(car)}>
            <CarImage>
              {photoUrl ? <img src={photoUrl} alt={displayName} /> : <PlaceholderIcon />}
            </CarImage>
            <CarInfo>
              <Tooltip arrow title={displayName} slotProps={TOOLTIP_SLOT_PROPS}>
                <CarName>{displayName}</CarName>
              </Tooltip>
              {!!plate && (
                <Tooltip arrow title={plate} slotProps={TOOLTIP_SLOT_PROPS}>
                  <CarPlate>{plate}</CarPlate>
                </Tooltip>
              )}
              <StatusBadge variant={status}>{getCarStatusLabel(status)}</StatusBadge>
            </CarInfo>
            {onRemoveCar && (
              <Tooltip
                arrow
                title="Supprimer le véhicule"
                placement="bottom"
                slotProps={TOOLTIP_SLOT_PROPS}
              >
                <RemoveButton
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveCar(car);
                  }}
                >
                  <SvgIcon icon={TrashIcon} color="inherit" />
                </RemoveButton>
              </Tooltip>
            )}
          </CarCard>
        );
      })}
    </CarListContainer>
  );
};

export default CarList;
